import type { ReactNode } from 'react';
import type { AgentInfo } from '../../api';
import { AGENT_NAME_MAX_LENGTH } from '../../api';
import { ConnectPasswordFields, type ConnectPasswordControl } from '../ConnectPassword';
import { SELF_CONNECT_BLOCKED, SELF_DEVICE_BADGE } from '../myConnection/useMyDeviceCode';
import { RenameButton, RenameEditor, type RenameState } from './RenameField';

/** 관리 기기 아이콘·설명 — ID 항목(🔖)과 한눈에 구별되게(사용자 결정 2026-10-06). */
export const MANAGED_ICON = '🖥';
export const MANAGED_TITLE = '관리 기기 — 이 조직에 등록된 기기(무인 접속·권한·차단)';

/**
 * 관리 기기 한 줄(설계서 §14.20 — 2026-09-15 기기 목록 행을 트리로 옮기며 떼어 냄).
 * 접속은 기기 id 로(무인 비밀번호). 관리 단추(이름·차단·제거·그룹 이동)는 관리자만 — 숨김은 편의, 검사는 서버.
 *
 * @param picker 그룹 이동 중이면 고르기 칸(단추 자리를 대신한다)
 */
export function AgentRow({
  a,
  isAdmin,
  busy,
  isSelf,
  rowBusy,
  rename,
  pw,
  picker,
  onToggleAnon,
  onToggleDisabled,
  onRemoveFromOrg,
  onStartMove,
}: {
  a: AgentInfo;
  isAdmin: boolean;
  busy: boolean;
  isSelf: boolean;
  rowBusy: boolean;
  rename: RenameState;
  pw: ConnectPasswordControl;
  picker: ReactNode | null;
  onToggleAnon: () => void;
  onToggleDisabled: () => void;
  onRemoveFromOrg: () => void;
  onStartMove: () => void;
}) {
  const label = a.deviceName || a.deviceCode;
  return (
    <div
      className="list-item gtree-row"
      title={a.disabled ? '차단된 기기입니다' : isSelf ? SELF_CONNECT_BLOCKED : '더블클릭하여 접속'}
      // 차단된 기기·이 PC 는 더블클릭 접속도 막는다(버튼만 막으면 우회된다). 이름 편집 중에도 접속하지 않는다.
      onDoubleClick={() => !busy && !a.disabled && !isSelf && !rename.active && pw.ask(a.id, label)}
      style={a.disabled ? { opacity: 0.55 } : undefined}
      data-agent-id={a.id}
    >
      <span className={`dot ${a.disabled ? 'err' : a.online ? 'ok' : 'err'}`} />
      <span className="gtree-icon" title={MANAGED_TITLE} aria-label="관리 기기">
        {MANAGED_ICON}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {rename.active ? (
          <RenameEditor rename={rename} busy={rowBusy} maxLength={AGENT_NAME_MAX_LENGTH} />
        ) : (
          <div className="name">
            {a.deviceName || '(이름 없음)'}
            {isSelf && (
              <span className="badge brand" title={SELF_CONNECT_BLOCKED} style={{ marginLeft: 8 }}>
                {SELF_DEVICE_BADGE}
              </span>
            )}
            {/* [이름 변경] 은 오른쪽 단추 줄로 옮겼다(2026-10-12 — 이름 옆이라 잘못 눌림). */}
            {a.disabled && (
              <span className="hint" style={{ marginLeft: 8, color: 'var(--err)' }}>
                차단됨
              </span>
            )}
            {/* 코드 접속 차단은 기기 차단과 다른 축 — 기기가 통째로 차단됐으면 중복 안내를 피해 숨긴다. */}
            {a.anonDisabled && !a.disabled && (
              <span className="hint" style={{ marginLeft: 8 }}>
                코드 접속 차단됨
              </span>
            )}
          </div>
        )}
        <div className="sub">
          {a.deviceCode}
          {a.appVersion ? ` · v${a.appVersion}` : ''} ·{' '}
          {a.disabled
            ? `차단 ${a.disabledAt ? new Date(a.disabledAt).toLocaleString() : ''}`
            : a.online
              ? '온라인'
              : '오프라인'}
          {a.anonDisabled && !a.disabled
            ? ` · 코드 접속 차단 ${a.anonDisabledAt ? new Date(a.anonDisabledAt).toLocaleString() : ''}`
            : ''}
          {a.renamedAt ? ' · 이름 직접 지정' : ''}
        </div>
      </div>
      {picker ??
        (pw.target === a.id ? (
          <ConnectPasswordFields ctl={pw} busy={busy} />
        ) : (
          <>
            {/* ⚠️ 두 "차단" 단추는 이름으로 구분돼야 한다 — 기기 차단 = 전부 / 코드 접속 차단 = 등록 사용자는 그대로. */}
            {isAdmin && (
              <>
                <button
                  className="ghost"
                  onClick={onToggleAnon}
                  disabled={rowBusy}
                  title={
                    a.anonDisabled
                      ? '기기 코드를 아는 사람이 다시 접속을 요청할 수 있게 합니다(노출이 늘어나는 방향)'
                      : '기기 코드만 아는 사람의 접속 요청을 막습니다. 등록 사용자 접속은 그대로 열려 있습니다'
                  }
                >
                  {rowBusy ? '처리 중…' : a.anonDisabled ? '코드 접속 허용' : '코드 접속 차단'}
                </button>
                <button
                  className="ghost"
                  onClick={onToggleDisabled}
                  disabled={rowBusy}
                  title={a.disabled ? '차단을 해제해 다시 접속할 수 있게 합니다' : '이 기기의 접속을 차단합니다(분실·퇴사·유출 대응)'}
                >
                  {rowBusy ? '처리 중…' : a.disabled ? '차단 해제' : '기기 차단'}
                </button>
                <button className="ghost danger" onClick={onRemoveFromOrg} disabled={rowBusy} title="이 조직에서만 뺍니다(기기와 다른 조직의 등록은 그대로)">
                  조직에서 제거
                </button>
                <RenameButton
                  rename={rename}
                  busy={rowBusy}
                  title="기기 이름 변경(관리자 전용). 바꾼 이름은 기기가 다시 접속해도 유지됩니다."
                />
                <button className="ghost" onClick={onStartMove} disabled={rowBusy} title="다른 그룹으로 옮깁니다(끌어다 놓아도 됩니다)">
                  그룹 이동
                </button>
              </>
            )}
            <button
              onClick={() => pw.ask(a.id, label)}
              disabled={busy || !a.online || a.disabled || isSelf}
              title={isSelf ? SELF_CONNECT_BLOCKED : undefined}
            >
              접속
            </button>
          </>
        ))}
    </div>
  );
}
